// # Mentees List Component
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import { useAuthStore } from '../../../../auth/state';
import { useNavigate } from 'react-router-dom';
import './mentees_list.css';
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import cacheManager from '../../../../auth/cache_manager';
import { updateDoc } from 'firebase/firestore';
// Import icons
import { 
  FaUserGraduate, 
  FaCalendarAlt, 
  FaEnvelope, 
  FaPhone, 
  FaUniversity,
  FaGraduationCap,
  FaPassport,
  FaGlobe,
  FaTimes,
  FaUserCircle,
  FaCalendarCheck,
  FaChartLine
} from 'react-icons/fa';
// Import MentorMeetingModal component
import { MentorMeetingModal } from './mentor_meetings';

const MenteesList = () => {
  const { user, isTutor } = useAuthStore();
  const [mentees, setMentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMentee, setSelectedMentee] = useState(null);
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # New state for tabs and filters
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active' or 'expired'
  const [filters, setFilters] = useState({
    step: 'all',
    search: '',
    sortBy: 'newest'
  });

  // # Meeting modal state
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingMenteeStep, setMeetingMenteeStep] = useState('');
  const [mentorZoomUrl, setMentorZoomUrl] = useState('');
  const [mentorEmail, setMentorEmail] = useState('');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  useEffect(() => {
    const fetchMentees = async () => {
      if (!user?.uid || !user?.email) return;

      try {
        setLoading(true);
        
        // # Get subscription documents where the mentor email matches the current user's email
        const subscriptionsCollection = collection(db, 'subscriptions');
        
        // # Create a list to store all mentee data
        const menteesList = [];
        
        // # Get all subscriptions with the current user's email as mentor
        // # We need to query each subscription type separately
        
        // # Step 1 mentorship subscriptions
        const step1Query = query(
          subscriptionsCollection,
          where('subscriptions.step1_mentorship.mentor.email', '==', user.email),
          where('subscriptions.step1_mentorship.status', '==', 'active')
        );
        
        const step1Snapshot = await getDocs(step1Query);
        step1Snapshot.forEach(doc => {
          const data = doc.data();
          const subscription = data.subscriptions?.step1_mentorship;
          if (subscription) {
            menteesList.push({
              uid: doc.id,
              email: subscription.mentor?.menteeEmail || '',
              subscriptionType: 'step1_mentorship',
              startDate: subscription.startDate,
              endDate: subscription.endDate,
              step: 'Step 1',
              status: subscription.status || 'unknown'
            });
          }
        });
        
        // # Step 2 mentorship subscriptions
        const step2Query = query(
          subscriptionsCollection,
          where('subscriptions.step2_mentorship.mentor.email', '==', user.email),
          where('subscriptions.step2_mentorship.status', '==', 'active')
        );
        
        const step2Snapshot = await getDocs(step2Query);
        step2Snapshot.forEach(doc => {
          const data = doc.data();
          const subscription = data.subscriptions?.step2_mentorship;
          if (subscription) {
            menteesList.push({
              uid: doc.id,
              email: subscription.mentor?.menteeEmail || '',
              subscriptionType: 'step2_mentorship',
              startDate: subscription.startDate,
              endDate: subscription.endDate,
              step: 'Step 2',
              status: subscription.status || 'unknown'
            });
          }
        });
        
        // # OET subscriptions
        const oetQuery = query(
          subscriptionsCollection,
          where('subscriptions.OET.mentor.email', '==', user.email),
          where('subscriptions.OET.status', '==', 'active')
        );
        
        const oetSnapshot = await getDocs(oetQuery);
        oetSnapshot.forEach(doc => {
          const data = doc.data();
          const subscription = data.subscriptions?.OET;
          if (subscription) {
            menteesList.push({
              uid: doc.id,
              email: subscription.mentor?.menteeEmail || '',
              subscriptionType: 'OET',
              startDate: subscription.startDate,
              endDate: subscription.endDate,
              step: 'OET',
              status: subscription.status || 'unknown'
            });
          }
        });
        
        // # US Visa counselling subscriptions
        const visaQuery = query(
          subscriptionsCollection,
          where('subscriptions.US_VISA_Counselling.mentor.email', '==', user.email),
          where('subscriptions.US_VISA_Counselling.status', '==', 'active')
        );
        
        const visaSnapshot = await getDocs(visaQuery);
        visaSnapshot.forEach(doc => {
          const data = doc.data();
          const subscription = data.subscriptions?.US_VISA_Counselling;
          if (subscription) {
            menteesList.push({
              uid: doc.id,
              email: subscription.mentor?.menteeEmail || '',
              subscriptionType: 'US_VISA_Counselling',
              startDate: subscription.startDate,
              endDate: subscription.endDate,
              step: 'US Visa',
              status: subscription.status || 'unknown'
            });
          }
        });
        
        // # Also include expired subscriptions to show all mentees
        const expiredQueries = [
          query(
            subscriptionsCollection,
            where('subscriptions.step1_mentorship.mentor.email', '==', user.email),
            where('subscriptions.step1_mentorship.status', '==', 'expired')
          ),
          query(
            subscriptionsCollection,
            where('subscriptions.step2_mentorship.mentor.email', '==', user.email),
            where('subscriptions.step2_mentorship.status', '==', 'expired')
          ),
          query(
            subscriptionsCollection,
            where('subscriptions.OET.mentor.email', '==', user.email),
            where('subscriptions.OET.status', '==', 'expired')
          ),
          query(
            subscriptionsCollection,
            where('subscriptions.US_VISA_Counselling.mentor.email', '==', user.email),
            where('subscriptions.US_VISA_Counselling.status', '==', 'expired')
          )
        ];
        
        // # Process all expired subscription queries
        await Promise.all(expiredQueries.map(async (expiredQuery, index) => {
          const snapshot = await getDocs(expiredQuery);
          snapshot.forEach(doc => {
            const data = doc.data();
            let subscription;
            let subscriptionType;
            let step;
            
            switch(index) {
              case 0:
                subscription = data.subscriptions?.step1_mentorship;
                subscriptionType = 'step1_mentorship';
                step = 'Step 1';
                break;
              case 1:
                subscription = data.subscriptions?.step2_mentorship;
                subscriptionType = 'step2_mentorship';
                step = 'Step 2';
                break;
              case 2:
                subscription = data.subscriptions?.OET;
                subscriptionType = 'OET';
                step = 'OET';
                break;
              case 3:
                subscription = data.subscriptions?.US_VISA_Counselling;
                subscriptionType = 'US_VISA_Counselling';
                step = 'US Visa';
                break;
              default:
                return;
            }
            
            if (subscription) {
              menteesList.push({
                uid: doc.id,
                email: subscription.mentor?.menteeEmail || '',
                subscriptionType,
                startDate: subscription.startDate,
                endDate: subscription.endDate,
                step,
                status: subscription.status || 'expired'
              });
            }
          });
        }));
        
        // # Step 2: Get user data for each mentee
        const menteesWithUserData = await Promise.all(
          menteesList.map(async (mentee) => {
            try {
              // # Get user data
              const userRef = doc(db, 'users', mentee.uid);
              const userDoc = await getDoc(userRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  ...mentee,
                  // # Personal Information
                  firstName: userData.firstName || '',
                  lastName: userData.lastName || '',
                  middleName: userData.middleName || '',
                  username: userData.username || '',
                  photoURL: userData.photoURL || '/icons/pfp.png',
                  gender: userData.gender || '',
                  nationality: userData.nationality || '',
                  phoneNumber: userData.phoneNumber || '',
                  selectedCountry: userData.selectedCountry || { code: '', flag: '' },
                  email: userData.email || mentee.email || '',
                  
                  // # Academic Information
                  university: userData.university || '',
                  phase: userData.phase || '',
                  gpa: userData.gpa || '',
                  
                  // # Profile Status
                  isProfileComplete: userData.isProfileComplete || false,
                  emailVerified: userData.emailVerified || false,
                  
                  // # Metadata
                  created_at: userData.created_at || '',
                  metadata: userData.metadata || {}
                };
              }
              
              return mentee;
            } catch (error) {
              console.error(`Error fetching user data for mentee ${mentee.uid}:`, error);
              return mentee;
            }
          })
        );
        
        // # Step 3: Get complete subscription data for each mentee
        const completeData = await Promise.all(
          menteesWithUserData.map(async (mentee) => {
            try {
              // # Get subscription data
              const subscriptionRef = doc(db, 'subscriptions', mentee.uid);
              const subscriptionDoc = await getDoc(subscriptionRef);
              
              if (subscriptionDoc.exists()) {
                const subscriptionData = subscriptionDoc.data();
                const menteeSubscription = subscriptionData.subscriptions?.[mentee.subscriptionType] || {};
                
                return {
                  ...mentee,
                  // # Subscription Status
                  status: menteeSubscription.status || mentee.status || 'unknown',
                  
                  // # Subscription Details
                  subscriptionHistory: subscriptionData.history || [],
                  paymentStatus: menteeSubscription.paymentStatus || 'unknown',
                  subscriptionMetadata: menteeSubscription.metadata || {},
                  
                  // # Additional Subscription Info
                  lastPayment: menteeSubscription.lastPayment || null,
                  nextPayment: menteeSubscription.nextPayment || null,
                  subscriptionTier: menteeSubscription.tier || 'basic',
                  features: menteeSubscription.features || []
                };
              }
              
              return mentee;
            } catch (error) {
              console.error(`Error fetching subscription data for mentee ${mentee.uid}:`, error);
              return mentee;
            }
          })
        );
        
        // # Keep all subscriptions without deduplication
        setMentees(completeData);
        setError(null);
      } catch (error) {
        console.error('Error fetching mentees:', error);
        setError('Failed to fetch mentees data');
      } finally {
        setLoading(false);
      }
    };

    fetchMentees();
  }, [user?.uid, user?.email]);

  // # Fetch mentor profile data
  useEffect(() => {
    const fetchMentorProfile = async () => {
      if (!user?.uid) return;
      
      try {
        const mentorRef = doc(db, 'mentors', user.uid);
        const mentorDoc = await getDoc(mentorRef);
        
        if (mentorDoc.exists()) {
          const data = mentorDoc.data();
          setMentorZoomUrl(data.personal?.zoom_url || '');
          setMentorEmail(data.personal?.mentor_email || user.email || '');
        }
      } catch (err) {
        console.error("Error fetching mentor profile:", err);
      }
    };
    
    fetchMentorProfile();
  }, [user?.uid, user?.email]);

  // # Format date for display with time
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A' || dateString === 'none') return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // # Handle view profile click
  const handleViewProfile = (menteeId, username) => {
    // # Navigate to public profile page by username
    if (username) {
      navigate(`/profile/${username}`);
    } else {
      console.error('Cannot view profile: Missing username');
    }
  };

  // # Handle schedule meeting click
  const handleScheduleMeeting = (menteeId, menteeEmail, step) => {
    // # Store mentee info in session storage for the meeting modal to access
    sessionStorage.setItem('scheduleMeetingMenteeId', menteeId);
    sessionStorage.setItem('scheduleMeetingMenteeEmail', menteeEmail);
    
    // # Set the meeting type based on step
    let meetingType = 'step1';
    if (step === 'Step 2') {
      meetingType = 'step2';
    } else if (step === 'OET') {
      meetingType = 'oet';
    } else if (step === 'US Visa') {
      meetingType = 'visa';
    }
    
    // # Set meeting step for the modal
    setMeetingMenteeStep(meetingType);
    
    // # Show the meeting modal
    setShowMeetingModal(true);
  };

  // # Handle meeting creation success
  const handleMeetingCreated = () => {
    setShowMeetingModal(false);
    setError(null);
    // # Show success message or perform any other actions
  };

  // # Handle profile picture click
  const handleProfileClick = (mentee) => {
    setSelectedMentee(mentee);
  };

  // # Format subscription type for display
  const formatSubscriptionType = (type) => {
    switch(type) {
      case 'step1_mentorship':
        return { text: 'Step 1 Mentorship', color: '#4CAF50' }; // Green
      case 'step2_mentorship':
        return { text: 'Step 2 Mentorship', color: '#2196F3' }; // Blue
      case 'OET':
        return { text: 'OET Preparation', color: '#FF9800' }; // Orange
      case 'US_VISA_Counselling':
        return { text: 'US Visa Counseling', color: '#9C27B0' }; // Purple
      default:
        return { text: type, color: '#757575' }; // Grey for unknown types
    }
  };

  // # MenteeDetailsModal Component
  const MenteeDetailsModal = ({ mentee, onClose }) => {
    if (!mentee) return null;

    // # Format phone number with country code
    const formattedPhone = mentee.selectedCountry ? 
      `${mentee.selectedCountry.code} ${mentee.phoneNumber}` : 
      mentee.phoneNumber;

    // # Handle visit mentee click
    const handleVisitMentee = () => {
      // # Determine which step the mentee is on based on subscription type
      let stepPath = '';
      if (mentee.subscriptionType === 'step1_mentorship' || mentee.step === 'Step 1') {
        stepPath = isTutor ? 
          `/tutor/mentorship/step1/${mentee.username || mentee.uid}` :
          `/mentor/step1/${mentee.username || mentee.uid}`;
      } else if (mentee.subscriptionType === 'step2_mentorship' || mentee.step === 'Step 2') {
        stepPath = isTutor ?
          `/tutor/mentorship/step2/${mentee.username || mentee.uid}` :
          `/mentor/step2/${mentee.username || mentee.uid}`;
      } else if (mentee.subscriptionType === 'OET') {
        stepPath = isTutor ?
          `/tutor/mentorship/oet/${mentee.username || mentee.uid}` :
          `/mentor/oet/${mentee.username || mentee.uid}`;
      } else if (mentee.subscriptionType === 'US_VISA_Counselling') {
        stepPath = isTutor ?
          `/tutor/mentorship/visa/${mentee.username || mentee.uid}` :
          `/mentor/visa/${mentee.username || mentee.uid}`;
      } else {
        // # Default to step1 if we can't determine
        stepPath = isTutor ?
          `/tutor/mentorship/step1/${mentee.username || mentee.uid}` :
          `/mentor/step1/${mentee.username || mentee.uid}`;
        console.warn('Could not determine mentee step type, defaulting to Step 1');
      }
      
      // # Store the mentee ID in sessionStorage for the components to access the specific mentee
      sessionStorage.setItem('currentViewingMenteeId', mentee.uid);
      
      // # Navigate to the appropriate mentee dashboard in the same tab
      navigate(stepPath);
    };

    return (
      <div className="mntr_lst_modal-overlay" onClick={onClose}>
        <div className="mntr_lst_modal-content" onClick={e => e.stopPropagation()}>
          <div className="mntr_lst_modal-header">
            <div className="mntr_lst_modal-header-content">
              <img 
                src={mentee.photoURL || '/icons/pfp.png'} 
                alt={`${mentee.firstName || 'Mentee'}'s profile`} 
                className="mntr_lst_modal-avatar"
              />
              <div className="mntr_lst_modal-header-info">
                <h2>
                  <FaUserGraduate />
                  {`${mentee.firstName} ${mentee.lastName}`}
                </h2>
                <span className="mntr_lst_modal-username">@{mentee.username || 'username'}</span>
              </div>
            </div>
            <button className="mntr_lst_modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="mntr_lst_modal-body">
            {/* Personal Information */}
            <div className="mntr_lst_modal-section">
              <h3><FaUserCircle /> Personal Information</h3>
              <div className="mntr_lst_modal-info">
                <p>
                  <strong><FaEnvelope /> Email:</strong>
                  {mentee.email}
                </p>
                <p>
                  <strong><FaPhone /> Phone:</strong>
                  {formattedPhone || 'N/A'}
                </p>
                <p>
                  <strong><FaGlobe /> Nationality:</strong>
                  {mentee.nationality || 'N/A'}
                </p>
                <p>
                  <strong><FaPassport /> Gender:</strong>
                  {mentee.gender || 'N/A'}
                </p>
              </div>
            </div>

            {/* Academic Information */}
            <div className="mntr_lst_modal-section">
              <h3><FaGraduationCap /> Academic Information</h3>
              <div className="mntr_lst_modal-info">
                <p>
                  <strong><FaUniversity /> University:</strong>
                  {mentee.university || 'N/A'}
                </p>
                <p>
                  <strong><FaChartLine /> GPA:</strong>
                  {mentee.gpa || 'N/A'}
                </p>
                <p>
                  <strong><FaCalendarAlt /> Phase:</strong>
                  {mentee.phase || 'N/A'}
                </p>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="mntr_lst_modal-section">
              <h3><FaCalendarCheck /> Subscription Details</h3>
              <div className="mntr_lst_modal-info">
                <p>
                  <strong>Type:</strong>
                  <span style={{ 
                    color: formatSubscriptionType(mentee.subscriptionType).color,
                    fontWeight: 'bold'
                  }}>
                    {formatSubscriptionType(mentee.subscriptionType).text}
                  </span>
                </p>
                <p>
                  <strong>Status:</strong>
                  <span className={`mntr_lst_subscription-status mntr_lst_status-${mentee.status}`}>
                    {mentee.status}
                  </span>
                </p>
                <p>
                  <strong>Start Date:</strong>
                  {formatDate(mentee.startDate)}
                </p>
                <p>
                  <strong>End Date:</strong>
                  {formatDate(mentee.endDate)}
                </p>
              </div>
            </div>
          </div>

          <div className="mntr_lst_modal-footer">
            <button className="mntr_lst_modal-button secondary" onClick={() => handleViewProfile(mentee.uid, mentee.username)}>
              <FaUserCircle /> View Profile
            </button>
            <button className="mntr_lst_modal-button primary" onClick={() => handleScheduleMeeting(mentee.uid, mentee.email, mentee.step)}>
              <FaCalendarCheck /> Schedule Meeting
            </button>
            <button className="mntr_lst_modal-button visit" onClick={handleVisitMentee}>
              <FaUserGraduate /> Visit Mentee
            </button>
          </div>
        </div>
      </div>
    );
  };

  // # Filter mentees based on active/expired status and other filters
  const getFilteredMentees = () => {
    if (!mentees.length) return [];

    return mentees.filter(mentee => {
      // # Check if mentee is active or expired
      const isExpired = new Date(mentee.endDate) < new Date();
      
      // # Apply tab filter
      if (activeTab === 'active' && isExpired) return false;
      if (activeTab === 'expired' && !isExpired) return false;
      // # 'all' tab shows all mentees, so no filtering needed

      // # Apply step filter
      if (filters.step !== 'all' && mentee.step !== filters.step) return false;

      // # Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const fullName = `${mentee.firstName} ${mentee.lastName}`.toLowerCase();
        const email = mentee.email.toLowerCase();
        if (!fullName.includes(searchTerm) && !email.includes(searchTerm)) return false;
      }

      return true;
    }).sort((a, b) => {
      // # Apply sorting
      if (filters.sortBy === 'newest') {
        return new Date(b.startDate) - new Date(a.startDate);
      }
      return new Date(a.startDate) - new Date(b.startDate);
    });
  };

  // # Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // # Render filters section
  const FiltersSection = () => (
    <div className="mntr_lst_filters-section">
      {/* Step filter */}
      <select 
        value={filters.step}
        onChange={(e) => handleFilterChange('step', e.target.value)}
        className="mntr_lst_filter-select"
      >
        <option value="all">All Steps</option>
        <option value="Step 1">Step 1</option>
        <option value="Step 2">Step 2</option>
        <option value="OET">OET</option>
        <option value="US Visa">US Visa</option>
      </select>

      {/* Search filter */}
      <input
        type="text"
        value={filters.search}
        onChange={(e) => handleFilterChange('search', e.target.value)}
        placeholder="Search by name or email..."
        className="mntr_lst_filter-search"
      />

      {/* Sort filter */}
      <select
        value={filters.sortBy}
        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
        className="mntr_lst_filter-select"
      >
        <option value="newest">Newest First</option>
        <option value="oldest">Oldest First</option>
      </select>
    </div>
  );

  return (
    <div className={`mntr_mentor-container ${isDarkMode ? 'mntr_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mntr_mentor-main-content">
        <div className="mntr_lst_title-container">
          <h2>My Mentees</h2>
        </div>
        
        {error && (
          <div className="mntr_lst_error-message">
            <p>{error}</p>
          </div>
        )}
        
        {/* Tabs */}
        <div className="mntr_lst_tabs">
          <button 
            className={`mntr_lst_tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Mentees
            <span className="mntr_lst_tab-count">{mentees.length}</span>
          </button>
          <button 
            className={`mntr_lst_tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active Mentees
            <span className="mntr_lst_tab-count">
              {mentees.filter(m => new Date(m.endDate) >= new Date()).length}
            </span>
          </button>
          <button 
            className={`mntr_lst_tab ${activeTab === 'expired' ? 'active' : ''}`}
            onClick={() => setActiveTab('expired')}
          >
            Expired Mentees
            <span className="mntr_lst_tab-count">
              {mentees.filter(m => new Date(m.endDate) < new Date()).length}
            </span>
          </button>
        </div>

        {/* Filters */}
        <FiltersSection />
        
        {loading ? (
          <div className="mntr_lst_loading-state">
            <p>Loading mentees...</p>
          </div>
        ) : mentees.length === 0 ? (
          <div className="mntr_lst_empty-state">
            <p>No mentees assigned yet.</p>
          </div>
        ) : (
          <div className="mntr_lst_mentees-grid">
            {getFilteredMentees().map((mentee) => {
              const subscriptionInfo = formatSubscriptionType(mentee.subscriptionType);
              return (
                <div key={`${mentee.uid}-${mentee.subscriptionType}`} className="mntr_lst_mentee-card">
                  <div className="mntr_lst_mentee-profile">
                    <img 
                      src={mentee.photoURL || '/icons/pfp.png'} 
                      alt={`${mentee.firstName || 'Mentee'}'s profile`} 
                      className="mntr_lst_mentee-avatar"
                      onClick={() => handleProfileClick(mentee)}
                    />
                    <div className="mntr_lst_mentee-info">
                      <h3>{`${mentee.firstName || ''} ${mentee.lastName || ''}`}</h3>
                      <p>{mentee.email}</p>
                    </div>
                    <span className="mntr_lst_mentee-step" style={{ backgroundColor: subscriptionInfo.color, color: '#fff' }}>
                      {subscriptionInfo.text}
                    </span>
                  </div>
                  <div className="mntr_lst_mentee-details">
                    <p>Status: <span className={`mntr_lst_subscription-status mntr_lst_status-${mentee.status}`}>
                      {mentee.status}
                    </span></p>
                    <p>Start: {formatDate(mentee.startDate)}</p>
                    <p>End: {formatDate(mentee.endDate)}</p>
                  </div>
                  <div className="mntr_lst_mentee-actions">
                    <button 
                      className="mntr_lst_view-profile-button"
                      onClick={() => handleViewProfile(mentee.uid, mentee.username)}
                    >
                      View Profile
                    </button>
                    <button 
                      className="mntr_lst_schedule-meeting-button"
                      onClick={() => handleScheduleMeeting(mentee.uid, mentee.email, mentee.step)}
                    >
                      Schedule Meeting
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedMentee && (
        <MenteeDetailsModal 
          mentee={selectedMentee} 
          onClose={() => setSelectedMentee(null)} 
        />
      )}

      {/* Meeting Modal */}
      {showMeetingModal && (
        <MentorMeetingModal
          open={showMeetingModal}
          onClose={() => {
            setShowMeetingModal(false);
            sessionStorage.removeItem('scheduleMeetingMenteeId');
            sessionStorage.removeItem('scheduleMeetingMenteeEmail');
          }}
          meetingType={meetingMenteeStep}
          onSuccess={handleMeetingCreated}
          mentorUrl={mentorZoomUrl}
          mentorEmail={mentorEmail}
        />
      )}

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default MenteesList; 